import { Button } from "@repo/ui/components/Base/Button";
import { Input } from "@repo/ui/components/Base/Input";
import { PageHeader } from "@/components/page";
import type {
  LegislationNumberCounter,
  LegislationNumberingFormat,
  MinutesOrdering,
  Site,
  Tenant,
} from "@repo/types";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useRoleProtected } from "@/hooks/useRoleProtected";
import { config } from "@/config";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { TagSelection } from "@/components/tags";
import {
  type NumberFormat,
  numberFormats,
  numberFormatsMap,
} from "@repo/shared/formatNumber";
import {
  MinutesOrderingDropdown,
  minutesOrderingMap,
} from "@/components/meetings/MinutesOrderingDropdown";
import { useGetSite } from "@/hooks/queryHooks/siteHooks";
import { useGetTenantByFriendly } from "@/hooks/queryHooks/tenantHooks";
import { tenantRepo } from "@/repositories/tenantRepo";
import { siteRepo } from "@/repositories/siteRepo";

const legislationNumberingFormatsMap: Record<
  LegislationNumberingFormat,
  string
> = {
  number: "Number",
  yearNumber: "Year and number",
};

const legislationNumberingFormats = (
  Object.keys(legislationNumberingFormatsMap) as LegislationNumberingFormat[]
).map((value) => ({
  label: legislationNumberingFormatsMap[value],
  value,
}));

const legislationNumberingSelectStyle = "w-72 max-w-full";
const legislationLastNumberInputStyle = "w-44 max-w-full";

type LegislationLastNumberInputKey = "bill" | "ordinance" | "resolution";
type LegislationLastNumberInputs = Record<
  LegislationLastNumberInputKey,
  string
>;

const formatLegislationNumberCounter = (
  counter: LegislationNumberCounter | undefined,
  format: LegislationNumberingFormat
) => {
  const number = counter?.number ?? 0;

  if (format === "yearNumber") {
    return `${counter?.year ?? new Date().getFullYear()}-${String(
      number
    ).padStart(2, "0")}`;
  }

  return `${number}`;
};

const parseLegislationNumberInput = (
  value: string,
  format: LegislationNumberingFormat
): LegislationNumberCounter | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return format === "yearNumber"
      ? { year: new Date().getFullYear(), number: 0 }
      : { number: 0 };
  }

  if (format === "number") {
    if (/^\d+$/.test(trimmed)) {
      return {
        number: Number(trimmed),
      };
    }

    return null;
  }

  const yearNumberMatch = /^(\d{4})-(\d+)$/.exec(trimmed);
  if (yearNumberMatch) {
    return {
      year: Number(yearNumberMatch[1]),
      number: Number(yearNumberMatch[2]),
    };
  }

  return null;
};

const getLegislationLastNumberInputs = (
  tenant: Tenant | undefined
): LegislationLastNumberInputs => ({
  bill: formatLegislationNumberCounter(
    tenant?.lastBillNumber,
    tenant?.billNumberingFormat ?? "number"
  ),
  ordinance: formatLegislationNumberCounter(
    tenant?.lastOrdinanceNumber,
    tenant?.ordinanceNumberingFormat ?? "number"
  ),
  resolution: formatLegislationNumberCounter(
    tenant?.lastResolutionNumber,
    tenant?.resolutionNumberingFormat ?? "yearNumber"
  ),
});

const getLegislationLastNumberError = (format: LegislationNumberingFormat) =>
  format === "yearNumber" ? "Use YYYY-NN" : "Use N";

export const SettingsPage = () => {
  const { tenantId } = useParams();
  useRoleProtected("admin");

  const { data: tenantData, loaded: tenantLoaded } = useGetTenantByFriendly(
    tenantId ?? ""
  );
  const { data: siteData, loaded: siteLoaded } = useGetSite(
    tenantData?.id ?? ""
  );

  const [localSite, setLocalSite] = useState<Site | undefined>();
  const [localTenant, setLocalTenant] = useState<Tenant | undefined>();
  const [lastNumberInputs, setLastNumberInputs] =
    useState<LegislationLastNumberInputs>(() =>
      getLegislationLastNumberInputs(undefined)
    );
  const [initialized, setInitialized] = useState(false);
  const [editable, setEditable] = useState(false);
  const [loading, setLoading] = useState(false);

  useBlockUnload(editable);

  const billNumberingFormat = localTenant?.billNumberingFormat ?? "number";
  const ordinanceNumberingFormat =
    localTenant?.ordinanceNumberingFormat ?? "number";
  const resolutionNumberingFormat =
    localTenant?.resolutionNumberingFormat ?? "yearNumber";
  const invalidLastNumberInputs = {
    bill:
      parseLegislationNumberInput(
        lastNumberInputs.bill,
        billNumberingFormat
      ) === null,
    ordinance:
      parseLegislationNumberInput(
        lastNumberInputs.ordinance,
        ordinanceNumberingFormat
      ) === null,
    resolution:
      parseLegislationNumberInput(
        lastNumberInputs.resolution,
        resolutionNumberingFormat
      ) === null,
  };
  const hasInvalidLastNumberInput = Object.values(invalidLastNumberInputs).some(
    Boolean
  );

  useEffect(() => {
    if (siteLoaded && tenantLoaded && siteData && tenantData && !initialized) {
      const tenant = tenantData as Tenant;
      setLocalSite(siteData as Site);
      setLocalTenant(tenant);
      setLastNumberInputs(getLegislationLastNumberInputs(tenant));
      setInitialized(true);
    }
  }, [siteLoaded, tenantLoaded, siteData, tenantData, initialized]);

  useEffect(() => {
    if (siteLoaded && siteData) {
      setLocalSite(siteData as Site);
    }
  }, [siteLoaded, siteData]);

  const handleSave = async () => {
    if (hasInvalidLastNumberInput) return;

    setLoading(true);

    if (localSite && tenantData?.id) {
      await siteRepo.update(tenantData.id, {
        ...localSite,
        status: "draft",
      });
    }

    if (localTenant) {
      await tenantRepo.update(localTenant.id, {
        name: localTenant.name,
        timeZone: localTenant.timeZone,
        standardTags: localTenant.standardTags,
        numberFormat: localTenant.numberFormat,
        minutesOrdering: localTenant.minutesOrdering,
        billNumberingFormat: localTenant.billNumberingFormat,
        lastBillNumber: localTenant.lastBillNumber,
        ordinanceNumberingFormat: localTenant.ordinanceNumberingFormat,
        lastOrdinanceNumber: localTenant.lastOrdinanceNumber,
        resolutionNumberingFormat: localTenant.resolutionNumberingFormat,
        lastResolutionNumber: localTenant.lastResolutionNumber,
      });
    }

    setLoading(false);
    setEditable(false);
  };

  const timeZones =
    (
      Intl as { supportedValuesOf?: (prop: string) => string[] }
    ).supportedValuesOf?.("timeZone") ?? [];

  return (
    <div className="flex flex-col flex-grow">
      <PageHeader
        title={tenantData?.name ? `${tenantData.name}'s Settings` : "Settings"}
      >
        <Button
          disabled={loading || (editable && hasInvalidLastNumberInput)}
          onClick={() => {
            if (editable) {
              handleSave();
            } else {
              setEditable(true);
            }
          }}
        >
          {editable ? "Save Changes" : "Edit"}
        </Button>
      </PageHeader>
      <div className="flex flex-col gap-2 mt-4">
        <div className="flex flex-row justify-start items-center">
          {editable ? (
            <Input
              label="Organization Name"
              disabled={loading}
              value={localTenant?.name ?? ""}
              id="orgName"
              onChange={(evt) =>
                setLocalTenant(
                  (tenant) => ({ ...tenant, name: evt.target.value }) as Tenant
                )
              }
            />
          ) : (
            <p>
              Organization Name:{" "}
              <span className="ml-1 font-bold">{tenantData?.name}</span>
            </p>
          )}
        </div>
        <div className="flex flex-row justify-start items-center">
          {editable ? (
            <SelectInput
              label="Timezone"
              disabled={loading}
              value={localTenant?.timeZone ?? "America/New_York"}
              onValueChange={(value) =>
                setLocalTenant(
                  (tenant) => ({ ...tenant, timeZone: value }) as Tenant
                )
              }
              options={timeZones.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz}
                </SelectItem>
              ))}
            />
          ) : (
            <p>
              Timezone:{" "}
              <span className="ml-1 font-bold">
                {tenantData?.timeZone ?? "America/New_York"}
              </span>
            </p>
          )}
        </div>
        <div>
          <TagSelection
            disabled={!editable || loading}
            label="Tags"
            value={localTenant?.standardTags ?? []}
            onChange={(newTags) => {
              setLocalTenant(
                (tenant) => ({ ...tenant, standardTags: newTags }) as Tenant
              );
            }}
          />
        </div>
        <div className="flex flex-row justify-start items-center">
          {editable ? (
            <div>
              <SelectInput
                label="Numeric list format"
                disabled={loading}
                triggerStyle="border-2 border-secondary"
                value={localTenant?.numberFormat ?? "decimal"}
                onValueChange={(value) => {
                  setLocalTenant(
                    (tenant) =>
                      ({
                        ...tenant,
                        numberFormat: value as NumberFormat,
                      }) as Tenant
                  );
                }}
                options={numberFormats.map((format) => (
                  <SelectItem key={format.value} value={format.value}>
                    {format.label}
                  </SelectItem>
                ))}
              />
              <p className="italic max-w-96 mt-2 mb-5">
                This option is legacy, and will be removed in a future release.
                Numeric list formats are now managed in letterhead settings.
                <br />
                <a
                  className="underline text-blue-600"
                  href="./settings/letterheads"
                >
                  Visit Letterheads &raquo;
                </a>
              </p>
            </div>
          ) : (
            <p>
              Numeric list format:{" "}
              <span className="ml-1 font-bold">
                {numberFormatsMap[localTenant?.numberFormat ?? "decimal"]}
              </span>
            </p>
          )}
        </div>
        <div className="flex flex-row justify-start items-center">
          {editable ? (
            <MinutesOrderingDropdown
              label="Meeting minutes sort order"
              disabled={loading}
              value={localTenant?.minutesOrdering ?? "timestamps"}
              onValueChange={(value) => {
                setLocalTenant((tenant) => ({
                  ...tenant!,
                  minutesOrdering: value as MinutesOrdering,
                }));
              }}
            />
          ) : (
            <p>
              Meeting minutes sort order:{" "}
              <span className="ml-1 font-bold">
                {
                  minutesOrderingMap[
                    localTenant?.minutesOrdering ?? "timestamps"
                  ]
                }
              </span>
            </p>
          )}
        </div>
        <div className="flex flex-row justify-start items-center">
          {editable ? (
            <div className="flex flex-row flex-wrap items-start gap-3">
              <SelectInput
                label="Draft ordinance numbering format"
                disabled={loading}
                containerStyle={legislationNumberingSelectStyle}
                value={billNumberingFormat}
                onValueChange={(value) => {
                  const format = value as LegislationNumberingFormat;
                  const lastBillNumberInput = formatLegislationNumberCounter(
                    localTenant?.lastBillNumber,
                    format
                  );
                  const lastBillNumber =
                    parseLegislationNumberInput(lastBillNumberInput, format) ??
                    undefined;

                  setLastNumberInputs((inputs) => ({
                    ...inputs,
                    bill: lastBillNumberInput,
                  }));

                  setLocalTenant(
                    (tenant) =>
                      ({
                        ...tenant,
                        billNumberingFormat: format,
                        lastBillNumber,
                      }) as Tenant
                  );
                }}
                options={legislationNumberingFormats.map((format) => (
                  <SelectItem key={format.value} value={format.value}>
                    {format.label}
                  </SelectItem>
                ))}
              />
              <Input
                id="lastBillNumber"
                label="Last draft number"
                disabled={loading}
                type="text"
                placeholder="N or YYYY-NN"
                containerStyle={legislationLastNumberInputStyle}
                value={lastNumberInputs.bill}
                error={invalidLastNumberInputs.bill}
                errorMessage={getLegislationLastNumberError(
                  billNumberingFormat
                )}
                onChange={(evt) => {
                  const value = evt.target.value;
                  const counter = parseLegislationNumberInput(
                    value,
                    billNumberingFormat
                  );

                  setLastNumberInputs((inputs) => ({
                    ...inputs,
                    bill: value,
                  }));

                  if (!counter) return;

                  setLocalTenant(
                    (tenant) =>
                      ({
                        ...tenant,
                        lastBillNumber: counter,
                      }) as Tenant
                  );
                }}
              />
            </div>
          ) : (
            <p>
              Draft ordinance numbering format:{" "}
              <span className="ml-1 font-bold">
                {legislationNumberingFormatsMap[billNumberingFormat]}
              </span>
              <span className="ml-4">
                Last draft ordinance number:{" "}
                <span className="font-bold">
                  {formatLegislationNumberCounter(
                    localTenant?.lastBillNumber,
                    billNumberingFormat
                  )}
                </span>
              </span>
            </p>
          )}
        </div>
        <div className="flex flex-row justify-start items-center">
          {editable ? (
            <div className="flex flex-row flex-wrap items-start gap-3">
              <SelectInput
                label="Ordinance numbering format"
                disabled={loading}
                containerStyle={legislationNumberingSelectStyle}
                value={ordinanceNumberingFormat}
                onValueChange={(value) => {
                  const format = value as LegislationNumberingFormat;
                  const lastOrdinanceNumberInput =
                    formatLegislationNumberCounter(
                      localTenant?.lastOrdinanceNumber,
                      format
                    );
                  const lastOrdinanceNumber =
                    parseLegislationNumberInput(
                      lastOrdinanceNumberInput,
                      format
                    ) ?? undefined;

                  setLastNumberInputs((inputs) => ({
                    ...inputs,
                    ordinance: lastOrdinanceNumberInput,
                  }));

                  setLocalTenant(
                    (tenant) =>
                      ({
                        ...tenant,
                        ordinanceNumberingFormat: format,
                        lastOrdinanceNumber,
                      }) as Tenant
                  );
                }}
                options={legislationNumberingFormats.map((format) => (
                  <SelectItem key={format.value} value={format.value}>
                    {format.label}
                  </SelectItem>
                ))}
              />
              <Input
                id="lastOrdinanceNumber"
                label="Last ordinance number"
                disabled={loading}
                type="text"
                placeholder="N or YYYY-NN"
                containerStyle={legislationLastNumberInputStyle}
                value={lastNumberInputs.ordinance}
                error={invalidLastNumberInputs.ordinance}
                errorMessage={getLegislationLastNumberError(
                  ordinanceNumberingFormat
                )}
                onChange={(evt) => {
                  const value = evt.target.value;
                  const counter = parseLegislationNumberInput(
                    value,
                    ordinanceNumberingFormat
                  );

                  setLastNumberInputs((inputs) => ({
                    ...inputs,
                    ordinance: value,
                  }));

                  if (!counter) return;

                  setLocalTenant(
                    (tenant) =>
                      ({
                        ...tenant,
                        lastOrdinanceNumber: counter,
                      }) as Tenant
                  );
                }}
              />
            </div>
          ) : (
            <p>
              Ordinance numbering format:{" "}
              <span className="ml-1 font-bold">
                {legislationNumberingFormatsMap[ordinanceNumberingFormat]}
              </span>
              <span className="ml-4">
                Last ordinance number:{" "}
                <span className="font-bold">
                  {formatLegislationNumberCounter(
                    localTenant?.lastOrdinanceNumber,
                    ordinanceNumberingFormat
                  )}
                </span>
              </span>
            </p>
          )}
        </div>
        <div className="flex flex-row justify-start items-center">
          {editable ? (
            <div className="flex flex-row flex-wrap items-start gap-3">
              <SelectInput
                label="Resolution numbering format"
                disabled={loading}
                containerStyle={legislationNumberingSelectStyle}
                value={resolutionNumberingFormat}
                onValueChange={(value) => {
                  const format = value as LegislationNumberingFormat;
                  const lastResolutionNumberInput =
                    formatLegislationNumberCounter(
                      localTenant?.lastResolutionNumber,
                      format
                    );
                  const lastResolutionNumber =
                    parseLegislationNumberInput(
                      lastResolutionNumberInput,
                      format
                    ) ?? undefined;

                  setLastNumberInputs((inputs) => ({
                    ...inputs,
                    resolution: lastResolutionNumberInput,
                  }));

                  setLocalTenant(
                    (tenant) =>
                      ({
                        ...tenant,
                        resolutionNumberingFormat: format,
                        lastResolutionNumber,
                      }) as Tenant
                  );
                }}
                options={legislationNumberingFormats.map((format) => (
                  <SelectItem key={format.value} value={format.value}>
                    {format.label}
                  </SelectItem>
                ))}
              />
              <Input
                id="lastResolutionNumber"
                label="Last resolution number"
                disabled={loading}
                type="text"
                placeholder="N or YYYY-NN"
                containerStyle={legislationLastNumberInputStyle}
                value={lastNumberInputs.resolution}
                error={invalidLastNumberInputs.resolution}
                errorMessage={getLegislationLastNumberError(
                  resolutionNumberingFormat
                )}
                onChange={(evt) => {
                  const value = evt.target.value;
                  const counter = parseLegislationNumberInput(
                    value,
                    resolutionNumberingFormat
                  );

                  setLastNumberInputs((inputs) => ({
                    ...inputs,
                    resolution: value,
                  }));

                  if (!counter) return;

                  setLocalTenant(
                    (tenant) =>
                      ({
                        ...tenant,
                        lastResolutionNumber: counter,
                      }) as Tenant
                  );
                }}
              />
            </div>
          ) : (
            <p>
              Resolution numbering format:{" "}
              <span className="ml-1 font-bold">
                {legislationNumberingFormatsMap[resolutionNumberingFormat]}
              </span>
              <span className="ml-4">
                Last resolution number:{" "}
                <span className="font-bold">
                  {formatLegislationNumberCounter(
                    localTenant?.lastResolutionNumber,
                    resolutionNumberingFormat
                  )}
                </span>
              </span>
            </p>
          )}
        </div>
        <div className="flex">
          <a href={config.urls.billing} target="_blank" className="mt-10">
            <Button disabled={loading}>Manage Your Subscription</Button>
          </a>
        </div>
      </div>
    </div>
  );
};
